import EditorData from '@common/components/editor/editor.data';
import * as monaco from 'monaco-editor';
import { getIdentifiers, getSyntaxErrors } from './Implementation/LanguageSyntaxAnalyzer';
import { configuration, languageDef, missingFix, returnFix } from './Language.config';
import { functions, keywords } from './snippets/Language.snippets';

/**
 * Url da documentação de referência da linguagem do cálculo.
 */
export const docsUrl = 'https://github.com/Vitor-Xavier/CalculationEngine/wiki';

/**
 * Registra a linguagem do cálculo e configura seus tokens, definições, sugestões, hover, parâmetros etc.
 */
export const loadLanguage = () => {
  // Verifica se a linguagem já foi definida.
  if (!monaco.languages.getLanguages().some(({ id }) => id === 'language')) {
    // Registra a linguagem para o Monaco Editor.
    monaco.languages.register({ id: 'language' });

    // Define os tokens da linguagem, e como cada tipo é reconhecido, palavras-chave, funções, operadores etc.
    monaco.languages.setMonarchTokensProvider('language', languageDef);

    // Define configurações da linguagem, como comentários, fechamento de pares etc.
    monaco.languages.setLanguageConfiguration('language', configuration);

    // Registra sugestões de código.
    monaco.languages.registerCompletionItemProvider('language', {
      triggerCharacters: ['.', '@'],
      provideCompletionItems: (model, position) => {
        const supportConstants = EditorData.getItem('supportConstants');

        const supportConstantsMapped =
          Array.isArray(supportConstants) && supportConstants.length > 0
            ? [...supportConstants]
                .map((s) => [
                  { Nome: `@${s.Nome}`, Texto: s.Nome, Tipo: s.Lista ? 'lista' : 'objeto', Anulavel: true },
                  ...s.Colunas.map((c) => {
                    return {
                      Nome: `@${s.Nome}${s.Lista === true ? '[]' : ''}.${c.Nome}`,
                      Texto: `${s.Nome}${s.Lista === true ? '[$0]' : ''}.${c.Nome}`,
                      Tipo: c.Tipo,
                      Anulavel: c.Anulavel,
                    };
                  }),
                ])
                .reduce((a, b) => a.concat(b))
            : [];

        supportConstantsMapped.push({
          Nome: `@Exercicio`,
          Texto: `Exercicio`,
          Tipo: 'int',
          Anulavel: false,
        });
        supportConstantsMapped.push({
          Nome: `@DataCalculo`,
          Texto: `DataCalculo`,
          Tipo: 'datetime',
          Anulavel: false,
        });

        // Constantes de Suporte
        const wordPosition = model.getWordAtPosition(position);
        if (!wordPosition) {
          const valueInRange = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: position.column - 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });
          if (valueInRange === '@') {
            return {
              suggestions: [...supportConstantsMapped].map((item) => {
                return {
                  label: item.Nome,
                  detail: (item.Tipo || '') + (item.Anulavel ? '?' : ''),
                  kind: monaco.languages.CompletionItemKind.Property,
                  filterText: item.Nome,
                  sortText: item.Nome,
                  insertText: item.Texto,
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                };
              }),
            };
          } else if (valueInRange === '.') {
            const supportConstantName = model.getWordAtPosition({
              column: position.column - 1,
              lineNumber: position.lineNumber,
            });
            if (supportConstantName) {
              return {
                suggestions: [...supportConstantsMapped]
                  .filter((s) => s.Nome.startsWith(`@${supportConstantName.word}.`))
                  .map((item) => {
                    return {
                      label: item.Nome,
                      detail: (item.Tipo || '') + (item.Anulavel ? '?' : ''),
                      kind: monaco.languages.CompletionItemKind.Property,
                      filterText: item.Nome,
                      sortText: item.Nome,
                      insertText: item.Texto,
                      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                    };
                  }),
              };
            }
          }
        }

        const suggestions = [
          {
            label: 'identificador',
            kind: monaco.languages.CompletionItemKind.Text,
            insertText: '${0:identificador}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          },
          {
            label: 'texto',
            kind: monaco.languages.CompletionItemKind.Text,
            insertText: '"$0"',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          },
          {
            label: 'data',
            kind: monaco.languages.CompletionItemKind.Text,
            insertText: '${1:dia}/${2:mes}/${3:ano}',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          },
          // Snippets
          {
            label: 'var',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: 'var ${1:identificador} = ${0};',
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Declaração de variável',
            documentation: 'var identificador = valor;',
          },
          {
            label: 'se',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: ['se (${1:condicao}) {', '\t${2}', '}'].join('\n'),
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Instrução Se',
            documentation: ['se (condicao) {', '\t', '}'].join('\n'),
          },
          {
            label: 'sesenao',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: ['se (${1:condicao}) {', '\t${2}', '} senao {', '\t${3}', '}'].join('\n'),
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Instrução Se-Senao',
            documentation: ['se (condicao) {', '\t', '} senao {', '\t', '}'].join('\n'),
          },
          {
            label: 'enquanto',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: ['enquanto (${1:condicao}) {', '\t${2}', '}'].join('\n'),
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Instrução Enquanto',
            documentation: ['enquanto (condicao) {', '\t', '}'].join('\n'),
          },
          {
            label: 'parametro',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: [
              'parametro (${1:entidade}) {',
              '\tcaso ${2:condicao}: {',
              '\t\t${3};',
              '\t\tparar;',
              '\t}',
              '}',
            ].join('\n'),
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Instrução Parâmetro',
            documentation: [
              'parametro (entidade) {',
              '\tcaso valor: {',
              '\t\tbloco de código',
              '\t\tparar;',
              '\t}',
              '}',
            ].join('\n'),
          },
          {
            label: 'caso',
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: ['caso (${1:valor}) {', '\t${2};', '\tparar;', '}'].join('\n'),
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: 'Instrução Caso',
            documentation: ['caso (valor) {', '\tbloco de código', '\tparar;', '}'].join('\n'),
          },
        ]
          // Concatena sugestões de constantes de suporte
          .concat(
            [...supportConstantsMapped].map((item) => {
              return {
                label: item.Nome,
                detail: (item.Tipo || '') + (item.Anulavel ? '?' : ''),
                kind: monaco.languages.CompletionItemKind.Property,
                filterText: item.Nome,
                sortText: item.Nome,
                insertText: item.Texto,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              };
            })
          )
          // Concatena sugestões de funções de suporte
          .concat(
            functions.map((item) => {
              return {
                label: { name: item.nome, signature: item.assinatura },
                kind: monaco.languages.CompletionItemKind.Function,
                insertText: item.texto,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                detail: item.nome + item.assinatura,
                documentation: item.descricao,
              };
            })
          )
          // Concatena sugestões de palavras-chave
          .concat(
            keywords.map((item) => {
              return {
                label: item.nome,
                kind: monaco.languages.CompletionItemKind.Keyword,
                insertText: item.texto,
                detail: item.descricao,
              };
            })
          )
          // Concatena sugestões de variáveis locais
          .concat(
            getIdentifiers(model.getValue()).map((item) => {
              return {
                label: item,
                kind: monaco.languages.CompletionItemKind.Variable,
                insertText: item,
                detail: item,
              };
            })
          );
        return {
          suggestions: suggestions,
        };
      },
      resolveCompletionItem: (model, position, item) => {
        if (item.kind !== monaco.languages.CompletionItemKind.Property) return item;

        const wordPosition = model.getWordAtPosition(position);
        const valueInRange = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: (wordPosition?.startColumn || position.column) - 1,
          endLineNumber: position.lineNumber,
          endColumn: wordPosition?.startColumn || position.column,
        });

        let insertText = item.insertText;
        if (valueInRange === '.') {
          const texts = item.insertText.split('.');
          insertText = texts[texts.length - 1];
        } else if (valueInRange === '@') {
          insertText = item.insertText;
        } else {
          insertText = `@${item.insertText}`;
        }

        return {
          ...item,
          insertText: insertText,
        };
      },
    });

    // Registra a caixa de detalhes ao passar o mouse em cima de textos, e procura na lista funções uma que tenha esse nome para prover uma descrição.
    monaco.languages.registerHoverProvider('language', {
      provideHover: function (model, position) {
        const word = model.getWordAtPosition(position);
        if (word) {
          const fn = functions.find((item) => item.nome === word.word);
          if (fn) {
            return {
              range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
              contents: [
                { value: `(função) ${fn.nome}${fn.assinatura}` },
                { value: fn.descricao },
                fn.exemplo ? { value: `Exemplo: ${fn.exemplo}` } : undefined,
              ],
            };
          }
        }
      },
    });

    // Registra ações ao código, como sugestões de correções rápidos em determinados tipos de erro.
    monaco.languages.registerCodeActionProvider('language', {
      provideCodeActions: (model, range, context, token) => {
        // Busca por erros do tipo 'Faltando', que podem ser corrigidos ao inserir o caractere faltando.
        const actions = context.markers
          .filter(
            (error) =>
              error.message.startsWith('Faltando') && ['{', '[', '(', '}', ']', ')', ';'].includes(error.source)
          )
          .map((error) => missingFix(error, model.uri))
          // Busca por erro de falta de retorno na fórmula, corrigido com a inserção do retorno ao final.
          .concat(
            context.markers
              .filter((error) => error.message === 'A fórmula deve finalizar com um retorno')
              .map((error) => returnFix(error, model.uri))
          );
        return {
          actions: actions,
          dispose: () => {},
        };
      },
    });

    // Registra as definições da linguagem
    monaco.languages.registerDefinitionProvider('language', {
      provideDefinition: (model, position) => {
        const wordPosition = model.getWordAtPosition(position);

        if (wordPosition) {
          const range = new monaco.Range(
            position.lineNumber,
            wordPosition.startColumn,
            position.lineNumber,
            wordPosition.endColumn
          );

          const fn = functions.find((f) => f.nome === wordPosition.word);
          if (fn && fn.definicao) return { uri: monaco.Uri.parse(fn.definicao), range: range };

          const kw = keywords.find((k) => k.nome === wordPosition.word);
          if (kw && kw.definicao) return { uri: monaco.Uri.parse(kw.definicao), range: range };
        }

        return null;
      },
    });

    // Registra o provedor de sugestões de parâmetros para funções.
    monaco.languages.registerSignatureHelpProvider('language', {
      signatureHelpTriggerCharacters: ['(', ','],
      provideSignatureHelp: function (model, position) {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });
        // Busca por funções e quantas delas são encerradas, para encontrar a função para prover sugestões.
        const funcs = textUntilPosition.match(/_[\w]+/g);
        if ((funcs || []).length === 0) return;
        const closeParens = (textUntilPosition.match(/\)/g) || []).length;
        const currentFunction = funcs[funcs.length - closeParens - 1];

        // Elimina de forma recursiva por caracteres ',' dentro do texto, e então conta quantas restaram para a função em questão
        let texto = textUntilPosition;
        var regex = new RegExp(/_[\w]+\([\w\s.,@\[\]]*[,]?[\w\s.,@\[\]]*\)/);
        while (texto.match(regex)) {
          texto = texto.replace(regex, '');
        }
        const parametro = (texto.match(/,/g) || []).length;

        const wordAt = model.getWordUntilPosition({ lineNumber: position.lineNumber, column: position.column - 1 });

        // Verifica se a sugestão de parâmetro foi ativada pelo caractere '(', para fornecer sugestões de função para o parâmetro 0.
        if (textUntilPosition.endsWith('(') && wordAt.word) {
          const fns = functions
            .filter((f) => f.nome === wordAt.word)
            .map((f) => {
              return {
                label: f.nome + f.assinatura,
                documentation: f.descricao,
                parameters: f.parametros.map((p) => {
                  return {
                    label: p.nome,
                    documentation: p.descricao,
                  };
                }),
              };
            });
          return {
            value: {
              signatures: fns,
              activeSignature: 0,
              activeParameter: 0,
            },
            dispose: () => {},
          };
          // Verifica se a sugestão de parâmetro foi ativada pelo caractere ',', para fornecer sugestões de função conforme o parâmetro.
        } else if (textUntilPosition.endsWith(',') && currentFunction) {
          const fns = functions
            .filter((f) => f.nome === currentFunction)
            .map((f) => {
              return {
                label: f.nome + f.assinatura,
                documentation: f.descricao,
                parameters: f.parametros.map((p) => {
                  return {
                    label: p.nome,
                    documentation: p.descricao,
                  };
                }),
              };
            });
          return {
            value: {
              signatures: fns,
              activeSignature: 0,
              activeParameter: parametro,
            },
            dispose: () => {},
          };
        }
        return { value: { signatures: [], activeSignature: 0, activeParameter: 0 }, dispose: () => {} };
      },
    });

    // Formata o código da linguagem.
    monaco.languages.registerDocumentFormattingEditProvider('language', {
      provideDocumentFormattingEdits: (model) => {
        return [
          {
            text: formatText(model.getValue()),
            range: model.getFullModelRange(),
          },
        ];
      },
    });

    // Adiciona o link para a documentação da linguagem.
    monaco.languages.registerCodeLensProvider('language', {
      provideCodeLenses: (model, token) => {
        return {
          lenses: [
            {
              id: 'Linguagem Docs',
              range: {
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 1,
              },
              command: {
                id: EditorData.getItem('CodeLensCommand'),
                title: 'Linguagem Docs',
                tooltip: 'Ir para documentação...',
              },
            },
            {
              id: 'Editor Docs',
              range: {
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: 1,
                endColumn: 1,
              },
              command: {
                id: EditorData.getItem('EditorCommand'),
                title: 'Editor Docs',
                tooltip: 'Ir para documentação...',
              },
            },
          ],
          dispose: () => {},
        };
      },
      resolveCodeLens: (model, codeLens, token) => {
        return codeLens;
      },
    });
  }
};

/**
 * Chama a validação de sintaxe do Antlr, e verifica por identificadores.
 * @param {string} code Bloco de código
 */
export const validateSyntaxLanguage = (code, supportConstants) => {
  const syntaxErrors = getSyntaxErrors(code, supportConstants || []);

  const errors = [];
  syntaxErrors.forEach((element) => {
    errors.push({
      startLineNumber: element.linha,
      startColumn: element.coluna + 1,
      endLineNumber: element.linha,
      endColumn: element.colunaFim + 2,
      message: element.mensagem,
      severity: monaco.MarkerSeverity.Error,
      source: element.texto,
    });
  });

  // Retorna os erros para o editor, com base na análise do Antlr.
  return errors;
};
